import PropTypes from 'prop-types';
import React, { useCallback, useContext, useMemo, useState } from 'react';
import dayjs from 'dayjs';

import { AttributesContext } from 'common/contexts';
import { clone } from 'common/utils/generic';
import UIDEditModal from 'common/components/UIDEditModal';
import UIDLink from 'common/components/UIDLink';
import { DEFAULT_TIME, noop } from 'common/constants/constants';
import { ATTRIBUTE_PROPTYPE, ATTRIBUTE_TYPE_PROPTYPE, SECTION_PROPTYPE } from 'common/constants/commonPropTypes';
import { TIME_DATATYPE } from 'common/constants/datatypes';
import { dedupeAttributes } from '../utils/displayAttributes';
import { updateSingletonValue, updateValue, updateAddedValue } from './updateAttributeValueUtils';
import AttributeTypeInfo from './components/AttributeTypeInfo';
import SectionEditFields from './components/SectionEditFields';
import styles from './AttributesContainer.module.scss';

// TODO: port this to Typescript

// Merge the attributes and the added attributes into one
const combineSections = (sections, addedSections) => {
  const sectionsWithAddedSectionFalse = [...sections.map(section => ({ ...section, addedAttribute: false }))];
  const sectionsClone = clone(sectionsWithAddedSectionFalse);
  const addedSectionsClone = clone(addedSections);

  addedSectionsClone.forEach(addedSection => {
    addedSection.attribute_types.forEach(addedAttrType => {
      const section = sectionsClone.find(a => a.name === addedSection.name);
      if (!section) {
        sectionsClone.push({ ...addedSection, addedAttribute: true });
        return;
      }

      const attrType = section.attribute_types.find(attribute => attribute.name === addedAttrType.name);
      if (!attrType) {
        section.attribute_types.push(addedAttrType);
        section.addedAttribute = true;
        return;
      }

      attrType.attributes = addedAttrType.attributes;
      section.addedAttribute = true;
    });
  });

  // filter out attributes that are not editable
  return sectionsClone.map(section => {
    const sectionClone = clone(section);
    sectionClone.attribute_types = sectionClone.attribute_types.filter(attributeType => attributeType.is_editable);
    return sectionClone;
  });
};

const AttributesContainer = ({
  activeSections,
  attributeDateChanges,
  editUser,
  addedSections,
  setAddedSections,
  userAttributeTypes,
  setAttributeDateChanges,
  setUserAttributeTypes,
  showAttributeEndTime,
  showAttributeStartTime,
}) => {
  const [uidEditModalSection, setUIDEditModalSection] = useState(null);
  const [uidEditModalAttrType, setUIDEditModalAttrType] = useState(null);
  const appData = useContext(AttributesContext).attributes;
  const combinedInfo = appData.genres.reduce((prev, curr) => ({
    sections: [...prev.sections, ...curr.sections],
  }));
  const { sections } = combinedInfo;

  const getAttributes = useCallback(
    name => {
      const foundAttributeType = userAttributeTypes.find(attributeType => attributeType.name === name);
      if (!foundAttributeType) return [];

      if (!foundAttributeType.attributes.length && foundAttributeType.datatype === TIME_DATATYPE) {
        // TODO: this seems to be wrong, as this is returning an attribute type rather than an attribute
        return [{ attributes: [{ value: dayjs.utc(undefined, 'YYYY-MM-DD'), is_derived: 'false' }] }];
      }

      // Removing null or undefined attributes.
      return dedupeAttributes(foundAttributeType).attributes.filter(attribute => attribute.value);
    },
    [userAttributeTypes]
  );

  const getAddedAttributeValue = useCallback(
    (name, typeName) => {
      const foundSection = addedSections.find(section => section.name === name);
      const foundAttributeType = foundSection && foundSection.attribute_types.find(type => type.name === typeName);
      if (!foundAttributeType) {
        return null;
      }
      return foundAttributeType && foundAttributeType.attributes[0].value
        ? foundAttributeType.attributes[0].value
        : null;
    },
    [addedSections]
  );

  const getDateValue = attrTypeName => {
    const attribute = getAttributes(attrTypeName);
    // defensive check to see if attributes are null or empty.
    // Empty attribute happens when we delete the date. May be a TODO to make this better
    if (!attribute || !attribute.length) {
      return null;
    }
    if (attribute && attribute[0].value === DEFAULT_TIME) {
      return null;
    }
    return attribute && attribute[0].value && dayjs.utc(attribute[0].value, 'YYYY-MM-DD');
  };

  const renderUIDAttributeEditLink = (section, attrType) => {
    const attributes = getAttributes(attrType.name) || getAddedAttributeValue(section.name, attrType.name);
    if (attributes) {
      return (
        <div>
          {attributes.map((attribute, i) => (
            <UIDLink comma={attributes.length - 1 !== i} key={attribute.value} uid={attribute.value} />
          ))}
          <span className={styles.editButton}>
            <a
              href="/"
              onClick={e => {
                e.preventDefault();
                setUIDEditModalSection(section);
                setUIDEditModalAttrType(attrType);
              }}
            >
              Edit
            </a>
          </span>
        </div>
      );
    }

    return null;
  };

  const modalUIDValues = useMemo(() => {
    if (uidEditModalAttrType && uidEditModalSection) {
      return getAttributes(uidEditModalAttrType.name)
        || getAddedAttributeValue(uidEditModalSection.name, uidEditModalAttrType.name);
    }

    return [];
  }, [getAttributes, getAddedAttributeValue, uidEditModalAttrType, uidEditModalSection]);

  if (!editUser) {
    return activeSections.map(section => (
      <div className={styles.sectionContainer} key={section.name}>
        <div className={styles.description}>
          {section.description}
        </div>
        <div className={styles.sectionInfo}>
          {section.attribute_types.map(attributeType => (
            <AttributeTypeInfo
              attributeType={attributeType}
              key={attributeType.name}
              showAttributeEndTime={showAttributeEndTime}
              showAttributeStartTime={showAttributeStartTime}
            />
          ))}
        </div>
      </div>
    ));
  }

  return (
    <>
      <UIDEditModal
        attrTypeName={uidEditModalAttrType && uidEditModalAttrType.name}
        attributes={modalUIDValues}
        onClose={() => {
          setUIDEditModalSection(null);
          setUIDEditModalAttrType(null);
        }}
        singleton={uidEditModalAttrType && uidEditModalAttrType.singleton}
        updateValue={(attrTypeName, userIdOrIds) => {
          // small helper function to create an attribute object representing the userId
          const userIdToAttribute = uid => uid !== undefined ? { value: uid } : undefined;

          if (uidEditModalAttrType) {
            if (uidEditModalSection.addedAttribute) {
              updateAddedValue(
                uidEditModalSection.name,
                uidEditModalAttrType.name,
                userIdOrIds,
                addedSections,
                userAttributeTypes,
                setUserAttributeTypes,
                setAddedSections
              );
            } else if (uidEditModalAttrType && uidEditModalAttrType.singleton) {
              // an attribute object representing the userId
              const valueAttribute = userIdToAttribute(userIdOrIds);
              updateSingletonValue(userAttributeTypes, setUserAttributeTypes, attrTypeName, valueAttribute);
            } else {
              // an array of attribute objects representing the user ids
              const valueAttribute = userIdOrIds.map(userIdToAttribute);
              updateValue(attrTypeName, valueAttribute, userAttributeTypes, setUserAttributeTypes);
            }
          }
        }}
      />
      {combineSections(activeSections, addedSections).map(section => (
        <SectionEditFields
          addedSections={addedSections}
          attributeDateChanges={attributeDateChanges}
          getAddedAttributeValue={getAddedAttributeValue}
          getAttributes={getAttributes}
          getDateValue={getDateValue}
          key={section.name}
          renderUIDAttributeEditLink={renderUIDAttributeEditLink}
          section={section}
          sections={sections}
          setAddedSections={setAddedSections}
          setAttributeDateChanges={setAttributeDateChanges}
          setUserAttributeTypes={setUserAttributeTypes}
          showAttributeEndTime={showAttributeEndTime}
          showAttributeStartTime={showAttributeStartTime}
          userAttributeTypes={userAttributeTypes}
        />
      ))}
    </>
  );
};

AttributesContainer.propTypes = {
  activeSections: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    attribute_types: PropTypes.arrayOf(ATTRIBUTE_TYPE_PROPTYPE),
  })),
  attributeDateChanges: PropTypes.arrayOf(PropTypes.shape({})),
  setAttributeDateChanges: PropTypes.func,
  editUser: PropTypes.bool,
  addedSections: PropTypes.arrayOf(SECTION_PROPTYPE),
  setAddedSections: PropTypes.func.isRequired,
  showAttributeEndTime: PropTypes.bool,
  showAttributeStartTime: PropTypes.bool,
  userAttributeTypes: PropTypes.arrayOf(PropTypes.shape({
    name: PropTypes.string,
    attributes: PropTypes.arrayOf(ATTRIBUTE_PROPTYPE),
  })),
  setUserAttributeTypes: PropTypes.func.isRequired,
};
AttributesContainer.defaultProps = {
  addedSections: [],
  activeSections: [],
  attributeDateChanges: [],
  editUser: false,
  setAttributeDateChanges: noop,
  showAttributeEndTime: false,
  showAttributeStartTime: false,
  userAttributeTypes: [],
};
export default AttributesContainer;
